import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  FileXls,
  FilePdf,
  Users,
  FileText,
  Warning,
  Bank,
  ChartLine,
  Download,
  Car,
  TrendUp
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const ReportCard = ({ report, type, downloading, onDownload }) => {
  const Icon = report.icon;
  const isDownloading = downloading === report.id;
  const FileIcon = type === 'excel' ? FileXls : FilePdf;

  return (
    <div
      className={`card p-4 border-2 ${report.color} transition-all hover:shadow-md`}
      data-testid={`report-card-${report.id.replace('/', '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${report.color}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <FileIcon size={24} weight="duotone" className={type === 'excel' ? 'text-green-600' : 'text-red-600'} />
      </div>

      <h3 className="font-heading font-bold text-[#0F172A] text-sm mb-1">{report.title}</h3>
      <p className="text-xs text-[#64748B] mb-3">{report.description}</p>

      <button
        onClick={() => onDownload(report.id, report.filename)}
        disabled={isDownloading}
        className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
          type === 'excel'
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-red-600 hover:bg-red-700 text-white'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid={`download-${report.id.replace('/', '-')}`}
      >
        {isDownloading ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Descargando...
          </>
        ) : (
          <>
            <Download size={16} />
            {type === 'excel' ? 'Excel' : 'PDF'}
          </>
        )}
      </button>
    </div>
  );
};

const Reports = () => {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);

  const downloadReport = async (endpoint, filename) => {
    setDownloading(endpoint);
    try {
      const response = await axios.get(`${API}/api/reports/${endpoint}`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      // User-facing alert is the proper handling here; no need to also spam console.
      alert('Error al descargar el reporte. Por favor intenta de nuevo.');
    } finally {
      setDownloading(null);
    }
  };

  const excelReports = [
    {
      id: 'excel/clients',
      title: 'Listado de Clientes',
      description: 'Datos de contacto y estatus',
      icon: Users,
      filename: 'clientes.xlsx',
      color: 'bg-green-50 text-green-600 border-green-200'
    },
    {
      id: 'excel/contracts',
      title: 'Listado de Contratos',
      description: 'Cliente, vehículo y saldos',
      icon: FileText,
      filename: 'contratos.xlsx',
      color: 'bg-blue-50 text-blue-600 border-blue-200'
    },
    {
      id: 'excel/overdue',
      title: 'Cartera Vencida',
      description: 'Pagos atrasados con penalizaciones',
      icon: Warning,
      filename: 'cartera_vencida.xlsx',
      color: 'bg-red-50 text-red-600 border-red-200'
    },
    {
      id: 'excel/creditors',
      title: 'Listado de Acreedores',
      description: 'Montos, tasas y saldos',
      icon: Bank,
      filename: 'acreedores.xlsx',
      color: 'bg-purple-50 text-purple-600 border-purple-200'
    },
    {
      id: 'excel/collection',
      title: 'Reporte de Cobranza',
      description: 'Gestión de pagos pendientes',
      icon: Warning,
      filename: 'reporte_cobranza.xlsx',
      color: 'bg-orange-50 text-orange-600 border-orange-200'
    },
    {
      id: 'excel/income-statement',
      title: 'Estado de Resultados',
      description: 'Ingresos, egresos y balance',
      icon: ChartLine,
      filename: 'estado_resultados.xlsx',
      color: 'bg-teal-50 text-teal-600 border-teal-200'
    },
    {
      id: 'excel/profitability',
      title: 'Rentabilidad por Vehículo',
      description: 'ROI, margen y flujo por unidad',
      icon: TrendUp,
      filename: 'rentabilidad_vehiculos.xlsx',
      color: 'bg-emerald-50 text-emerald-600 border-emerald-200'
    }
  ];

  const pdfReports = [
    {
      id: 'pdf/collection',
      title: 'Reporte de Cobranza',
      description: 'Listado para gestión de cobranza',
      icon: Warning,
      filename: 'reporte_cobranza.pdf',
      color: 'bg-orange-50 text-orange-600 border-orange-200'
    },
    {
      id: 'pdf/income-statement',
      title: 'Estado de Resultados',
      description: 'Ingresos, egresos, utilidad y balance',
      icon: ChartLine,
      filename: 'estado_resultados.pdf',
      color: 'bg-teal-50 text-teal-600 border-teal-200'
    },
    {
      id: 'pdf/creditors',
      title: 'Reporte de Acreedores',
      description: 'Resumen de deudas y pagos',
      icon: Bank,
      filename: 'reporte_acreedores.pdf',
      color: 'bg-indigo-50 text-indigo-600 border-indigo-200'
    },
    {
      id: 'pdf/profitability',
      title: 'Rentabilidad por Vehículo',
      description: 'ROI y margen por unidad',
      icon: TrendUp,
      filename: 'rentabilidad_vehiculos.pdf',
      color: 'bg-emerald-50 text-emerald-600 border-emerald-200'
    }
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Reportes</h1>
        <p className="text-[#64748B]">Descarga reportes en Excel y PDF</p>
      </div>

      {/* Excel Reports */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <FileXls size={24} className="text-green-600" weight="duotone" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-[#0F172A]">Reportes Excel</h2>
            <p className="text-sm text-[#64748B]">Datos exportables para análisis</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          {excelReports.map((report) => (
            <ReportCard key={report.id} report={report} type="excel" downloading={downloading} onDownload={downloadReport} />
          ))}
        </div>
      </div>

      {/* PDF Reports */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <FilePdf size={24} className="text-red-600" weight="duotone" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-[#0F172A]">Reportes PDF</h2>
            <p className="text-sm text-[#64748B]">Documentos listos para imprimir</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {pdfReports.map((report) => (
            <ReportCard key={report.id} report={report} type="pdf" downloading={downloading} onDownload={downloadReport} />
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Nota:</strong> Los reportes se generan con los datos actuales del sistema. 
          El estado de resultados muestra el mes en curso.
        </p>
      </div>
    </div>
  );
};

export default Reports;
